import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import { HIGHLIGHT_MESSAGE_UNAVAILABLE_FOR_DATES } from 'components/OfferList/OfferListTiles/constants'
import BedbankCancellationPolicy from 'components/OfferPage/Bedbank/BedbankCancellationPolicy'
import { OptimizelyExperiments } from 'constants/optimizely'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import useMapSearchOfferUrl from 'hooks/Search/useMapSearchOfferUrl'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { buildBedbankCancellationPolicy } from 'lib/search/hotels'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import SearchTile from '../SearchTile'
import SearchTileHighlightBanner from '../shared/SearchTileHighlightBanner'
import SearchTileRowOverlineLocation from '../shared/SearchTileRowOverlineLocation'
import BedbankLTSSearchTileFeatures from './BedbankLTSSearchTileFeatures'
import BedbankSearchTileAction from './BedbankSearchTileAction'
import BedbankSearchTileFeatures from './BedbankSearchTileFeatures'
import BedbankSearchTilePriceStack from './BedbankSearchTilePriceStack'
import BedbankSearchTileUnavailabilityBanner from './BedbankSearchTileUnavailabilityBanner'
import ExperimentalBedbankLTSSearchTileFeatures from './ExperimentalBedbankLTSSearchTileFeatures'
import ExperimentalBedbankSearchTileFeatures from './ExperimentalBedbankSearchTileFeatures'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import SearchTileComparisonCheckbox from '../shared/SearchTileComparisonCheckbox'
import { getLoyaltyBedbankPackageId } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import useOfferRating from 'hooks/Offers/useOfferRating'
import { generalLog } from 'marketing/api'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
}

function BedbankSearchTile({ offer, filters }: Props) {
  const utmSource = useAppSelector(state => state.utm.source)
  const experimentsSearchTileCompactFeatures = !!useOptimizelyExperiment(
    OptimizelyExperiments.searchTileCompactFeatures,
    utmSource !== 'google'
  )
  const queryParams = useAppSelector((state) => state.router.location.query)
  const fullPath = useAppSelector((state) => state.router.location.pathname + state.router.location.search)
  const locations = useMemo<Array<string>>(() => {
    if (offer.property.locationTitleOverride) return [offer.property.locationTitleOverride]

    return [
      [offer.property.address.city, offer.property.address.countryName].filter(Boolean).join(', '),
    ]
  }, [offer])

  const mapUrl = useMapSearchOfferUrl(offer)

  const utmBp = useAppSelector(state => state.utm.bp)

  const shouldUseSearchPrices = getShouldUseSearchPrices(filters)

  const offerMetaData = useOfferMetaData(offer.id, filters)
  const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !filters?.flexibleNights
  const promotions = useBedbankRoomRatePromotions(offer.promotions, filters?.checkIn, filters?.checkOut, isAnytimeSearch)
  const hasPromotions = offerMetaData?.hasPromotions || !!Object.values(promotions).length

  const shouldForceFetchRate = !!(filters?.checkIn && filters?.checkOut)
  const {
    offerPrice,
    offerLoyaltyPrice,
    offerLoyaltyMemberPrice,
    shownRate,
    fetchingRates,
    mx,
  } = useBedbankOfferSearchPricing(offer, filters, undefined, shouldForceFetchRate)

  const cancellationPolicy = useMemo(() => {
    return buildBedbankCancellationPolicy(
      offerMetaData?.cancellationPolicyInfo,
      filters,
      offer.property.timezone,
    )
  }, [offerMetaData?.cancellationPolicyInfo, filters, offer.property.timezone])

  const hasDates = !!(filters?.checkIn && filters.checkOut)
  const isSoldOut = shouldUseSearchPrices ? !offerMetaData?.available : (!shownRate && hasDates && !fetchingRates)

  const highlightMessage = useMemo<string | undefined>(() => {
    if (
      filters?.propertyId &&
      !filters?.searchNearby &&
      offer.property?.id === filters.propertyId.split(':')[1]
    ) { // This is a property search result
      if (filters?.checkIn && filters?.checkOut) {
        if (isSoldOut) {
          return HIGHLIGHT_MESSAGE_UNAVAILABLE_FOR_DATES(offer.saleUnit)
        }
        return 'We found a great price for your hotel. Book now to secure this rate!'
      } else {
        return 'We found the property you were interested in. Select your dates to book now.'
      }
    }
  }, [filters, offer, isSoldOut])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    {
      price: offerLoyaltyPrice,
      memberPrice: offerLoyaltyMemberPrice,
      mx,
      loyaltyProductType: 'hotel_lpp_eps',
      packageId: getLoyaltyBedbankPackageId(offer.property.id, shownRate?.supplierCode),
      offerId: offer.id,
    },
  ],
  [offerLoyaltyPrice, offerLoyaltyMemberPrice, mx, offer.property.id, offer.id, shownRate?.supplierCode])

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (isSoldOut) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        availability: false,
        extendedAvailability: offerMetaData?.extendedAvailability,
        price: undefined,
        memberPrice: undefined,
        duration: undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSoldOut])

  useEffect(() => {
    if (highlightMessage && offerPrice && fullPath.includes('metasearch')) {
      const priceTotal = queryParams.priceTotal || 0
      generalLog({
        status: 'info',
        message: 'GoogleHotelSearchPage: highlightedPackage',
        payload: { ...queryParams, fullPath, priceReceived: offerPrice, priceDifference: Number(priceTotal) - offerPrice },
      })
    }
  }, [highlightMessage, offerPrice, queryParams, fullPath])

  const hidePricing = useHideAnytimePrices(offer.type, offerMetaData?.hidePricing)
  const rating = useOfferRating(offer, offerMetaData)

  const { onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)

  const handleSelectDatesClick = useCallback(() => {
    if (onAnyHotelSelectDatesPrompt) {
      onAnyHotelSelectDatesPrompt(offer)
    }
  }, [onAnyHotelSelectDatesPrompt, offer])

  return <SearchTile
    state={highlightMessage ? 'highlighted' : 'default'}
    action={
      !isSoldOut &&
      (
        <BedbankSearchTileAction
          offer={offer}
          filters={filters}
          utmBp={utmBp}
          onSelectDatesClick={handleSelectDatesClick}
          hidePricing={hidePricing}
        />
      )
    }
    banner={!!highlightMessage && <SearchTileHighlightBanner description={highlightMessage} />}
    bookmarkAction={<BookmarkButton offer={offer} />}
    cancellationPolicyLabel={(!experimentsSearchTileCompactFeatures && offer.productType === 'limited_time_special' && (shownRate || cancellationPolicy)) && <BedbankCancellationPolicy
      rate={shownRate ?? cancellationPolicy}
      showNonCancellation
    />}
    bottomExtension={isSoldOut &&
      <BedbankSearchTileUnavailabilityBanner
        offer={offer}
        unavailableReason={offerMetaData?.unavailableReason}
        filters={filters}
      />}
    features={<>
      {!hasPromotions && <>
        {!experimentsSearchTileCompactFeatures && <BedbankSearchTileFeatures offer={offer} filters={filters} />}
        {experimentsSearchTileCompactFeatures && <ExperimentalBedbankSearchTileFeatures offer={offer} filters={filters} />}
      </>}
      {hasPromotions && <>
        {!experimentsSearchTileCompactFeatures && <BedbankLTSSearchTileFeatures offer={offer} />}
        {experimentsSearchTileCompactFeatures && <ExperimentalBedbankLTSSearchTileFeatures offer={offer} filters={filters} />}
      </>}
    </>}
    meta={<LoyaltyPointsEarn size="M" calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />}
    images={offer.images}
    overline={<SearchTileRowOverlineLocation locations={locations} mapUrl={mapUrl} />}
    pricePoint={!isSoldOut && <BedbankSearchTilePriceStack offer={offer} filters={filters} hidePrice={hidePricing} />}
    productLabel={hasPromotions && <ProductTypeLabel productType={offer.productType} />}
    rating={rating}
    title={offer.name}
    comparisonCheckbox={<SearchTileComparisonCheckbox offerType={offer.type} offerId={offer.id} name={offer.name} filters={filters} />}
    offer={offer}
  />
}

export default BedbankSearchTile
